import {
  ContractorType,
  LegalEntityList,
  PersonList
} from '@insurance/api/requests'
import { AddPersonButton } from '@insurance/components'
import { Text } from '@shared/components'
import { Checkbox, Divider, Flex, Form, Row } from 'antd'
import { Add } from 'iconsax-react'
import { useTranslation } from 'react-i18next'

import { ContractorLegalPerson } from './ContractorLegalPerson'
import { ContractorLocation } from './ContractorLocation'
import { ContractorPerson } from './ContractorPerson'
import { ContractorTypeSelect } from './ContractorTypeSelect'

type Props = {
  isEditable?: boolean
  resident?: boolean
  disableContractorType?: boolean
}

type CommonFieldsProps = {
  fieldName: string
  showFieldsKey: string

  title?: React.ReactNode
  addButtonTitle?: React.ReactNode
  locationTitle?: React.ReactNode
  sameTitle?: React.ReactNode
  showAddress?: boolean
  revers?: boolean
  notRequiredIdno?: boolean
} & Props

const CommonFields = ({
  isEditable,
  resident = true,
  disableContractorType,
  fieldName,
  showFieldsKey,

  title,
  addButtonTitle,
  locationTitle,
  sameTitle,
  showAddress,
  revers,
  notRequiredIdno
}: CommonFieldsProps) => {
  const formInstance = Form.useFormInstance()
  const contractor = Form.useWatch(fieldName, { preserve: true })
  const showFields = Form.useWatch(showFieldsKey, {
    preserve: true
  })

  const contractorType = contractor?.content_type
  const personValue =
    contractorType === ContractorType.PERSON
      ? contractor?.person
      : contractor?.legal_entity

  const handleAddContractor = (person: PersonList | LegalEntityList) => {
    if ('date_of_birth' in person) person.date_of_birth = undefined

    if (contractorType === ContractorType.PERSON)
      formInstance.setFieldValue([fieldName, 'person'], person)

    if (contractorType === ContractorType.LEGALENTITY)
      formInstance.setFieldValue([fieldName, 'legal_entity'], person)
  }

  const show = revers ? !showFields : showFields

  return (
    <>
      <Flex
        align="center"
        gap={12}
        justify="space-between"
        style={{ paddingBottom: 12 }}
      >
        <Text size={16} strong>
          {title}
        </Text>

        {show && (
          <AddPersonButton
            disabled={isEditable}
            personType={contractorType}
            type="link"
            icon={<Add size={16} />}
            value={personValue}
            onAdd={handleAddContractor}
          >
            {addButtonTitle}
          </AddPersonButton>
        )}
      </Flex>

      <Form.Item name={showFieldsKey} valuePropName="checked">
        <Checkbox disabled={isEditable}>{sameTitle}</Checkbox>
      </Form.Item>

      {show && (
        <>
          <ContractorTypeSelect
            disableContractorType={disableContractorType}
            isEditable={isEditable}
            contractorKey={fieldName}
          />

          {contractorType === ContractorType.PERSON && (
            <>
              <ContractorPerson
                isEditable={isEditable}
                contractorKey={fieldName}
              />

              {showAddress && (
                <Row gutter={12} align="bottom">
                  <ContractorLocation
                    disabled={isEditable}
                    locationKey={[fieldName, 'person']}
                    title={locationTitle}
                  />
                </Row>
              )}
            </>
          )}

          {contractorType === ContractorType.LEGALENTITY && (
            <ContractorLegalPerson
              isEditable={isEditable}
              contractorKey={fieldName}
              resident={resident}
              notRequiredIdno={notRequiredIdno}
            >
              {showAddress && (
                <ContractorLocation
                  disabled={isEditable}
                  locationKey={[fieldName, 'legal_entity']}
                  title={locationTitle}
                />
              )}
            </ContractorLegalPerson>
          )}
        </>
      )}
    </>
  )
}

export const BeneficiaryAndInsuredPerson = ({ ...props }: Props) => {
  const { t } = useTranslation()

  return (
    <>
      <Divider />
      <CommonFields
        fieldName="beneficiary"
        showFieldsKey="set_beneficiary_data"
        title={t('beneficiary')}
        addButtonTitle={t('add_existing_beneficiary')}
        sameTitle={t('set_beneficiary_data')}
        locationTitle={t('beneficiary_domicile')}
        {...props}
      />

      <CommonFields
        fieldName="insured_person"
        showFieldsKey="insured_same_as_contractor"
        title={t('insured_person')}
        addButtonTitle={t('add_existing_insured_person')}
        sameTitle={t('use_same_insured_person_as_contractor')}
        locationTitle={t('insured_person_domicile')}
        showAddress
        revers
        {...props}
      />
      <Divider />
    </>
  )
}
